import { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import CentredSpinner from '../Components/CentredSpinner';
import { useHttpUtils } from '../Utilities/useHttpUtils';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
} from 'chart.js';

import ChartDataLabelsPlugin from 'chartjs-plugin-datalabels';
import ChartZoomPlugin from 'chartjs-plugin-zoom';
import ChartAnnotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-luxon';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors,
    ChartDataLabelsPlugin,
    ChartZoomPlugin,
    ChartAnnotationPlugin
);

interface QualityGraphProps {
    lotNumber: string;
    component: string;
}

interface GraphData {
    time: string;
    type: string;
    data: string;
    labValue: string;
    identifier: string;
}

interface BatchInfo {
    start: string;
    end: string;
    name: string;
    color: string;
};

function QualityGraph({ lotNumber, component }: QualityGraphProps) {
    const { t } = useTranslation();

    const { fetchDataAuthenticated } = useHttpUtils();

    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ datasets: [] });
    const [options, setOptions] = useState();

    const dataSetColors = ["#FEC64E", "#37C2BE", "#EF7572", "#2A62B0", "#FC1C19"];
    const dataSetFillColors = ["#FEC64E80", "#37C2BE80", "#EF757280", "#2A62B080", "#FC1C1980"];
    const batchFillColors = ["#FEC64E20", "#37C2BE20", "#EF757220", "#2A62B020", "#FC1C1920"];

    const setupBatchInfo = (batchInfo: BatchInfo[], options: any) => {

        batchInfo.forEach((element: BatchInfo) => {
            var fillColor = batchFillColors[options.plugins.annotation.annotations.length % batchFillColors.length];

            options.plugins.annotation.annotations.push({
                type: 'box',
                backgroundColor: fillColor,
                borderWidth: 0,
                xMax: element.end,
                xMin: element.start,
                label: {
                    drawTime: 'afterDraw',
                    display: true,
                    content: element.name,
                    position: {
                        x: 'center',
                        y: 'start'
                    }
                }
            });
        });
    };

    const create1MinDataSet = (element: GraphData, dataPoint: any,  color: string, fillColor: string) => {
        return {
            label: t(element.type),
            data: [dataPoint],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            borderColor: color,
            backgroundColor: fillColor,
            spanGaps: false,    // could use true and apply 'segment' styling if we wanted dashed lines through gaps
            datalabels: {
                labels: {
                    title: null
                }
            }
        }
    };

    const createLabResultDataSet = (element: GraphData, dataPoint: any, color: string, fillColor: string) => {
        return {
            label: t(element.type),
            data: [dataPoint],
            pointRadius: 2,
            showLine: false,
            borderColor: color,
            backgroundColor: fillColor,
            datalabels: {
                align: 'top',
                offset: 10,
                formatter: function (value: any, context: any) {
                    return context.dataset.data[context.dataIndex].identifier; // Use the "identifier" field as the lab result label
                },
                color: "#ffffff",   // text colour in the lab result label
                borderColor: color,
                backgroundColor: fillColor,
                borderWidth: 1,
                borderRadius: 2
            }
        }
    };

    const addToDataSet = (element: GraphData, graphData: any) => {
        var dataSetIdx = graphData.datasets.findIndex((dataSet: any) => t(element.type) === dataSet.label);

        var dataPoint = { x: element.time, y: element.data !== null ? element.data : element.labValue, identifier: element.identifier }

        if (dataSetIdx === -1) {

            // New data set, create it. If data is null, it's a lab value and should be represented as a point with a data label, not a line
            var color = dataSetColors[graphData.datasets.length % dataSetColors.length];
            var fillColor = dataSetFillColors[graphData.datasets.length % dataSetFillColors.length];

            if (element.data !== null) {
                // Note this also generates the hi & low limit lines, although they only have start and end values (2 datapoints)
                graphData.datasets.push(create1MinDataSet(element, dataPoint, color, fillColor));
            }
            else {
                // Lab value series
                graphData.datasets.push(createLabResultDataSet(element, dataPoint, color, fillColor));
            }

        } else {
            // Existing data set, add to it
            graphData.datasets[dataSetIdx].data.push(dataPoint);
        }
    }

    const setupChart = (data: any) => {
        let graphData: any = {
            labels: [],
            datasets: [],
        };

        let newOptions: any = {
            responsive: true,
            plugins: {
                legend: { position: 'bottom' as const },
                zoom: {
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: 'x'
                    },
                    limits: {
                        x: { min: 'original', max: 'original' }
                    }
                },
                annotation: {
                    common: { drawTime: 'beforeDraw' },
                    annotations: []
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: "hour",
                        parser: "yy-MM-dd HH:mm:ss"
                    }
                }
            }
        };

        if (data.batchInfo) {
            setupBatchInfo(data.batchInfo, newOptions);
        }

        if (data.dataSet) {
            data.dataSet.forEach((element: GraphData) => {
                addToDataSet(element, graphData);
            });
        }

        setOptions(newOptions);
        setData(graphData);
    };

    useEffect(() => {

        setLoading(true);

        const fetchData = async () => {
            await fetchDataAuthenticated(`api/v1/Graph?lotNumber=${lotNumber}&component=${component}`)
                .then(async response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error(await response.json());
                    }
                })
                .then(data => {
                    if (data) {
                        setupChart(data);
                    }
                })
                .catch(error => {
                    setError(true);
                    setErrorMessage(error.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        fetchData();
    }, [lotNumber, component]);

    return error ? <Alert variant="danger">{errorMessage}</Alert>
        : loading ? <CentredSpinner />
        : <Line options={options} data={data} />;
}

export default QualityGraph;